/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { Config, EditableComponent, MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { PageParamsContext } from '../../modules/context/PageContext';
import type { InternetBarModalProps } from './components/InternetBarModal/InternetBarModal.types';
import {
  connectBtnTriggerLSPPostParam,
  getDataInCookie,
  getEnrolledUserFlag,
  getIsUpgrade,
  getLoyaltyEnabledForConferenceZone,
  getRegionEnrollmentSourceCode,
  getSFRoomLastNameValidationFlag,
  getZonePropertyTier,
  handleLoginBtnTrigger,
  isWeChatEnabled,
  loginBtnTriggerLSPPostParams,
  setDataInCookie,
  setLoginModalInformation,
  settlementCurrencyHandler,
  submitDynamicForm,
} from './utils/handler';
import { userExperienceConstants } from '../../modules/utils/constants';
import { INTERNET_BAR_INITIAL_STATE, LOYALTY_ENABLED_ZONES } from './utils/constant';
import {
  EnrollmentSourceCodeDivisionMapping,
  EnrollmentSourceCodeMapping,
  InternetBarProps,
  InternetBarStateTypes,
  InternetPricingDataProps,
  PricingInfoProps,
} from './InternetBar.types';
import { StyledInternetBar } from './InternetBar.styles';
import { inStayMockResponse, settlementCurrenyDetailsMock } from './__mock__/InternetBar.model';
import { useSession } from '@marriott/mi-headless-utils';
import { useServiceStore, serviceStore } from '../../modules/store/serviceStore';
import { InternetBarWeChatPseudoLink } from './components/InternetBarModal/InternetBarWeChatPseudoLink';
import { openPpv5LinkInSameTab } from '../../modules/utils/common';
import {
  analyticsTrackingIdentifier,
  analyticsTrackingUniqueDescription,
  getInternetBarTrackingIdentifier,
} from '../../modules/utils/constants';
import { buildBonvoyLookupReturnUrl, verifyReservationBonvoyLookupService } from './utils/bonvoyLookupHandler';

const InternetBarModal = dynamic<InternetBarModalProps>(
  () => import('./components/InternetBarModal/InternetBarModal').then(mod => mod.InternetBarModal),
  { ssr: false }
);

const InternetBarPreAgeExperience = dynamic(
  () =>
    import('./components/InternetBarPreAgeExperience/InternetBarPreAgeExperience').then(
      mod => mod.InternetBarPreAgeExperience
    ),
  { ssr: false }
);

const InternetBarAgeExperience = dynamic(
  () =>
    import('./components/InternetBarAgeExperience/InternetBarAgeExperience').then(mod => mod.InternetBarAgeExperience),
  { ssr: false }
);

const InternetBarMarketingApproachExperience = dynamic(
  () =>
    import('./components/InternetBarMarketingApproach/InternetBarMarketingApproach').then(
      mod => mod.InternetBarMarketingApproachExperience
    ),
  { ssr: false }
);

const InternetBarConservativeApproachExperience = dynamic(
  () =>
    import('./components/InternetBarConservativeApproach/InternetBarConservativeApproach').then(
      mod => mod.InternetBarConservativeApproachExperience
    ),
  { ssr: false }
);

const InternetBarConferenceAgeExperience = dynamic(
  () =>
    import('./components/InternetBarConferenceAgeExperience/InternetBarConferenceAgeExperience').then(
      mod => mod.InternetBarConferenceAgeExperience
    ),
  { ssr: false }
);

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const InternetBar: FC<InternetBarProps> = (pageProps: InternetBarProps) => {
  const { preProcessorResponse } = useContext(PageParamsContext);
  const setServiceData = useServiceStore((state: serviceStore) => state.setservice);
  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;

  const {
    experienceSegment = '',
    state = '',
    marsha = '',
    cna = '',
    sameTab = '',
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
    enableLoyalty = false,
    enableLoyaltyPostConnect = false,
    enableLoyaltyConference = false,
    enableLoyaltyConferencePostConnect = false,
    propertyInfo,
    enrollmentSourceCodeMapping,
  } = preProcessorResponse || {};
  const { datalayer, isACDLenabled } = useSession();

  const [updatedCurrentStatemapping, setUpdatedCurrentStateMapping] = useState(
    urlQueryParams?.['LR']?.toUpperCase() === 'STRICT_AO'
      ? {
          ...currentStateMapping,
          noLoginInterfaceModal: false,
          showLoginModal: false,
          showAccessModal: true,
          accessModal: {
            default: true,
            acErrorMessage: urlQueryParams?.['MV'] && urlQueryParams?.['MV'] === 'ACCESSERR' ? true : false,
          },
          loginModal: {
            connectNowLabel: false,
            default: false,
            separatorLabel: false,
          },
        }
      : { ...currentStateMapping }
  );

  const [internetBarDataValues, setInternetBarDataValues] = useState<Record<string, any>>({});
  const [showInternetBarModal, setShowInternetBarModal] = useState<boolean>(false);
  const [currentStateHasModalOption, setCurrentStateHasModalOption] = useState<boolean>(false);
  const [loyaltyCurrentStateMapping, setLoyaltyCurrentStateMapping] = useState({
    ...updatedCurrentStatemapping,
  });
  const [isNoLoginInterfaceModal, setIsNoLoginInterfaceModal] = useState<boolean>(
    updatedCurrentStatemapping?.noLoginInterfaceModal ?? false
  );
  const [modalType, setModalType] = useState('');
  const [settlementCurrencyData, setSettlementCurrencyData] = useState<PricingInfoProps | null>(null);
  const [showSettlementCurrencyErrorModal, setShowSettlementCurrencyErrorModal] = useState<boolean>(false);
  const [internetPricingData, setInternetPricingData] = useState<InternetPricingDataProps>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [weChatEnabled, setWeChatEnabled] = useState<boolean>(false);
  const [weChatEnrollmentCode, setWeChatEnrollmentCode] = useState<string>('');
  const [computedZoneMapping, setComputedZoneMapping] = useState<Record<string, any> | null>(null);
  const [loyaltyForConferenceZone, setLoyaltyForConferenceZone] = useState<boolean>(false);
  const [isLoyaltyEnabledForConference] = useState<boolean>(
    getLoyaltyEnabledForConferenceZone(
      enableLoyaltyConference ? enrollmentSourceCodeMapping?.loyalty : enrollmentSourceCodeMapping?.nonloyalty,
      propertyInfo?.data?.basicInformation?.globalDivision,
      propertyInfo?.data?.contactInformation?.address?.country?.code
    ) ?? false
  );
  const [updatedEnrollmentSourceCode, setUpdatedEnrollmentSourceCode] = useState<string>(
    (preProcessorResponse &&
    (enableLoyalty ||
      enableLoyaltyPostConnect ||
      ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference))
      ? pageProps?.dataLists?.loyaltyEnrollmentSourceCode
      : pageProps?.dataLists?.enrollmentSourceCode) ?? ''
  );
  const [isCaptiveCNPortal, setIsCaptiveCNPortal] = useState<boolean>(false);

  const { asPath } = useRouter();

  const handleCloseBtnClick = () => {
    setShowInternetBarModal(false);
    setModalType('');
  };

  const handleLinks = (enrollmentSourceCode: string, zoneMapping?: Record<string, any>, z?: string) => {
    const { dataLists } = pageProps;
    const { segmentId } = dataLists;
    const propertyPricingTier = zoneMapping?.[z ?? 'GUEST'];
    const anchorList = (document.getElementsByTagName('a') ?? []) as unknown as HTMLAnchorElement[];
    const joinBanner = document.getElementById('Join-Banner') as unknown as HTMLElement;
    const shouldOpenPpv5LinkInSameTab = openPpv5LinkInSameTab(sameTab, cna);
    let updatedLink = '';
    for (const element of anchorList) {
      let description = element?.getAttribute('href') ?? '';
      element.classList.add('custom_click_track');
      if (shouldOpenPpv5LinkInSameTab) {
        element?.setAttribute('target', '_self');
      }
      if (description && description?.toString()?.includes('/loyalty/createAccount/createAccountPage1.mi')) {
        if (
          description &&
          description?.toString()?.includes('enrollmentSourceCode') &&
          description?.toString()?.includes('returnToUrl')
        ) {
          description = description?.split('?')[0];
        }

        const sourceSystem = dataLists?.sourceSystem ? `sourceSystem=${dataLists?.sourceSystem}&` : '';
        if (preProcessorResponse && (enableLoyalty || (enableLoyaltyConference && isLoyaltyEnabledForConference))) {
          const urlQueryParams = `${sourceSystem}enrollmentSourceCode=${enrollmentSourceCode}&returnToUrl=${encodeURIComponent(
            `${asPath}&ppv5Enrollment=true`
          )}`;
          updatedLink = description?.includes('?')
            ? `${description}&${urlQueryParams}`
            : `${description}?${urlQueryParams}`;
        } else {
          const urlQueryParams = `segmentId=${segmentId}&${sourceSystem}enrollmentSourceCode=${enrollmentSourceCode}&returnToUrl=${encodeURIComponent(
            asPath
          )}`;
          updatedLink = description?.includes('?')
            ? `${description}&${urlQueryParams}`
            : `${description}?${urlQueryParams}`;
        }
        element?.setAttribute('href', updatedLink);
        element?.setAttribute(
          'data-custom_click_track_value',
          `${
            joinBanner?.contains(element)
              ? analyticsTrackingIdentifier.joinBanner
              : getInternetBarTrackingIdentifier(
                  preProcessorResponse?.state,
                  analyticsTrackingIdentifier?.internetBar,
                  propertyPricingTier
                )
          }|${analyticsTrackingUniqueDescription.joinNowLink}|internal|`
        );
      } else if (description && description?.toString()?.includes('/sign-in.mi')) {
        const isLoggedInUser =
          preProcessorResponse &&
          preProcessorResponse?.userSegment &&
          preProcessorResponse?.userSegment === 'Logged In';
        if (description.toString()?.includes('_PPV5URL_')) {
          element?.setAttribute(
            'href',
            `${description
              .toString()
              ?.replace(
                /_PPV5URL_/g,
                isLoggedInUser ? encodeURIComponent(asPath) : encodeURIComponent(encodeURIComponent(asPath))
              )}`
          );
        }
        element?.setAttribute(
          'data-custom_click_track_value',
          `${
            joinBanner?.contains(element)
              ? analyticsTrackingIdentifier.joinBanner
              : getInternetBarTrackingIdentifier(
                  preProcessorResponse?.state,
                  analyticsTrackingIdentifier?.internetBar,
                  propertyPricingTier
                )
          }|${analyticsTrackingUniqueDescription.signInLink}|internal|`
        );
      }
    }
    return updatedLink;
  };

  const handleLoginBonvoyLookupService = async (
    roomNumber: string,
    lastName: string,
    marsha: string,
    propertyTier: string
  ) => {
    const response = await verifyReservationBonvoyLookupService(roomNumber, lastName, marsha?.toUpperCase());
    const { memberShipLevel = 'unknown', prefillData = false, status = '', success = false, code = '' } = response;
    const isUpgrade = getIsUpgrade(internetBarDataValues);
    const loyaltyBackBtnData =
      !internetBarDataValues?.['LR'] &&
      propertyTier?.toUpperCase() === 'SF' &&
      (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference))
        ? connectBtnTriggerLSPPostParam(
            propertyTier?.toUpperCase() === 'SF' && enableLoyalty ? true : isNoLoginInterfaceModal ?? false,
            {
              ...internetBarDataValues,
            }
          )
        : loginBtnTriggerLSPPostParams({ roomNumber, lastName }, { ...internetBarDataValues }, currentStateMapping);

    const loyaltyWelcomeBtnData =
      !internetBarDataValues?.['LR'] &&
      propertyTier?.toUpperCase() === 'SF' &&
      (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference))
        ? connectBtnTriggerLSPPostParam(
            propertyTier?.toUpperCase() === 'SF' && enableLoyalty ? true : isNoLoginInterfaceModal ?? false,
            {
              ...internetBarDataValues,
              APS: 'N',
            }
          )
        : loginBtnTriggerLSPPostParams(
            { roomNumber, lastName },
            { ...internetBarDataValues, APS: 'N' },
            currentStateMapping
          );

    if (success) {
      let memberStatus = memberShipLevel?.toLowerCase();
      // BE response will return empty string if the memberShipLevel is unknown
      if (memberStatus === '' || memberStatus === null || memberStatus === undefined) {
        memberStatus = 'unknown';
      }
      if (status?.toLowerCase() === 'success') {
        setLoginModalInformation({ roomNumber, lastName }, marsha);
        if (memberStatus?.toLowerCase() === 'unknown') {
          setDataInCookie('PPv5LoyaltyBackBtnData', loyaltyBackBtnData ?? '');
          setDataInCookie('PPv5LoyaltyWelcomeData', loyaltyWelcomeBtnData ?? '');
        }
        const loyaltyEnrollmentSourceCode = internetPricingData?.['data']?.enrollmentSourceCode
          ? internetPricingData?.['data']?.enrollmentSourceCode
          : internetBarDataValues?.['loyaltyEnrollmentSourceCode'];
        const joinNowLink = handleLinks(loyaltyEnrollmentSourceCode);
        const returnToUrlForBonvoyLookup = buildBonvoyLookupReturnUrl(joinNowLink, prefillData);
        const { ppv5Enrollment, Z } = internetBarDataValues;
        const isLoyaltyEnabled =
          (enableLoyalty || ppv5Enrollment) && LOYALTY_ENABLED_ZONES?.includes((Z || '')?.toUpperCase());

        switch (memberStatus?.toUpperCase()) {
          case 'UNKNOWN':
            window.open(returnToUrlForBonvoyLookup, '_self');
            break;

          // case 'N':
          //   loginBtnTrigger({ roomNumber, lastName }, { ...internetBarDataValues, APS: 'N' }, currentStateMapping);
          //   break;

          // case 'E':
          //   loginBtnTrigger({ roomNumber, lastName }, { ...internetBarDataValues, APS: 'E' }, currentStateMapping);
          //   break;

          default:
            handleLoginBtnTrigger(
              { ...internetBarDataValues, APS: memberStatus?.toUpperCase() === 'E' ? 'E' : 'N' },
              isNoLoginInterfaceModal,
              isLoyaltyEnabled,
              false,
              { roomNumber, lastName },
              currentStateMapping
            );
        }
      } else {
        if (code === '404') {
          handleCloseBtnClick();
          const bonvoyLookupFailureCount = getDataInCookie('bonvoyLookupFailureCount');
          if (bonvoyLookupFailureCount) {
            setDataInCookie('bonvoyLookupFailureCount', (parseInt(bonvoyLookupFailureCount as string) + 1).toString());
          } else {
            setDataInCookie('bonvoyLookupFailureCount', '1');
          }
          setTimeout(() => {
            setUpdatedCurrentStateMapping({
              ...updatedCurrentStatemapping,
              loginModal: {
                ...updatedCurrentStatemapping['loginModal'],
                errorMessage: true,
              },
            });
            setModalType('LOGERR');
            setShowInternetBarModal(true);
          }, 500);
        }
      }
    } else {
      const endpoint = isUpgrade ? internetBarDataValues['U_URLEndpoint'] : internetBarDataValues['postEndpoint'];
      submitDynamicForm(loyaltyBackBtnData, endpoint);
    }
  };

  const renderAgeExperience = (loyaltyAgeExperience: string = '') => {
    let userExperience = loyaltyAgeExperience;
    let ageExperience = null;
    const { Z } = internetBarDataValues;
    const { enableLoyaltyConference } = preProcessorResponse;

    if (Z && Z?.toUpperCase('CONFERENCE') && enableLoyaltyConference) {
      userExperience = 'conference-age-experience';
    }

    const { baselineControlUX, marketingWithIncentive, marketingWithoutIncentive, conferenceAgeExperience } =
      userExperienceConstants;

    switch (userExperience) {
      case baselineControlUX:
        ageExperience = (
          <InternetBarAgeExperience
            setCurrentStateHasModalOption={setCurrentStateHasModalOption}
            setModalType={setModalType}
            setShowInternetBarModal={setShowInternetBarModal}
            internetBarCurrentStateData={{ ...internetBarDataValues }}
            internetPricingData={internetPricingData as unknown as InternetPricingDataProps}
            handleLinks={handleLinks}
            resizeImage={pageProps?.resizeImage}
            currentStateMapping={
              urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
              typeof window !== 'undefined' &&
              getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
                ? { ...loyaltyCurrentStateMapping }
                : { ...updatedCurrentStatemapping }
            }
            enableLoyaltyJoinNowBtn={!!settlementCurrencyData || (!settlementCurrencyData && hasError)}
            {...pageProps}
          />
        );
        break;

      case marketingWithIncentive:
      case marketingWithoutIncentive:
        ageExperience = (
          <InternetBarMarketingApproachExperience
            setCurrentStateHasModalOption={setCurrentStateHasModalOption}
            setModalType={setModalType}
            setShowInternetBarModal={setShowInternetBarModal}
            internetBarCurrentStateData={{ ...internetBarDataValues }}
            internetPricingData={internetPricingData as unknown as InternetPricingDataProps}
            handleLinks={handleLinks}
            resizeImage={pageProps?.resizeImage}
            currentStateMapping={
              urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
              typeof window !== 'undefined' &&
              getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
                ? { ...loyaltyCurrentStateMapping }
                : { ...updatedCurrentStatemapping }
            }
            enableLoyaltyJoinNowBtn={!!settlementCurrencyData || (!settlementCurrencyData && hasError)}
            {...pageProps}
          />
        );
        break;

      case conferenceAgeExperience:
        ageExperience = (
          <InternetBarConferenceAgeExperience
            setCurrentStateHasModalOption={setCurrentStateHasModalOption}
            setModalType={setModalType}
            setShowInternetBarModal={setShowInternetBarModal}
            internetBarCurrentStateData={{ ...internetBarDataValues }}
            internetPricingData={internetPricingData as unknown as InternetPricingDataProps}
            handleLinks={handleLinks}
            currentStateMapping={
              urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
              typeof window !== 'undefined' &&
              getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
                ? { ...loyaltyCurrentStateMapping }
                : { ...updatedCurrentStatemapping }
            }
            resizeImage={pageProps?.resizeImage}
            enableLoyaltyJoinNowBtn={!!settlementCurrencyData || (!settlementCurrencyData && hasError)}
            {...pageProps}
          />
        );
        break;

      default:
        ageExperience = (
          <InternetBarConservativeApproachExperience
            setCurrentStateHasModalOption={setCurrentStateHasModalOption}
            setModalType={setModalType}
            setShowInternetBarModal={setShowInternetBarModal}
            internetBarCurrentStateData={{ ...internetBarDataValues }}
            internetPricingData={internetPricingData as unknown as InternetPricingDataProps}
            handleLinks={handleLinks}
            resizeImage={pageProps?.resizeImage}
            currentStateMapping={
              urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
              typeof window !== 'undefined' &&
              getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
                ? { ...loyaltyCurrentStateMapping }
                : { ...updatedCurrentStatemapping }
            }
            enableLoyaltyJoinNowBtn={!!settlementCurrencyData || (!settlementCurrencyData && hasError)}
            {...pageProps}
          />
        );
        break;
    }
    return ageExperience;
  };

  const renderPreAgeExp = (
    <InternetBarPreAgeExperience
      setCurrentStateHasModalOption={setCurrentStateHasModalOption}
      setModalType={setModalType}
      setShowInternetBarModal={setShowInternetBarModal}
      internetBarCurrentStateData={internetBarDataValues}
      internetPricingData={internetPricingData as unknown as InternetPricingDataProps}
      handleLinks={handleLinks}
      resizeImage={pageProps?.resizeImage}
      currentStateMapping={
        urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
        typeof window !== 'undefined' &&
        getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
          ? { ...loyaltyCurrentStateMapping }
          : { ...updatedCurrentStatemapping }
      }
      {...pageProps}
    />
  );

  const [renderInternetBarComponent, setRenderInternetBarComponent] = useState(
    !(preProcessorResponse && (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference))) &&
      renderPreAgeExp
  );

  const initializeInternetBarData = () => {
    // function to validate if the URL query params are as per the AEM current state mapping
    const urlAsPerMapping = (MV: string = '', ERR: string = '') => {
      if (MV || ERR) {
        let hasModalOption = false;

        switch (MV) {
          case 'LOGIN':
          case 'LOGERR':
            hasModalOption = updatedCurrentStatemapping && updatedCurrentStatemapping?.showLoginModal ? true : false;
            break;

          case 'ACCESS':
          case 'ACCESSERR':
            hasModalOption = updatedCurrentStatemapping && updatedCurrentStatemapping?.showAccessModal ? true : false;
            break;

          case 'REQSMS':
          case 'REQSMSERR':
            hasModalOption =
              updatedCurrentStatemapping && updatedCurrentStatemapping?.showReqAccessModal ? true : false;
            break;

          case 'INT_ERR':
            hasModalOption =
              (updatedCurrentStatemapping &&
                (updatedCurrentStatemapping?.showUpgradeGenErr ||
                  updatedCurrentStatemapping?.showUpgradeLRErr ||
                  updatedCurrentStatemapping?.showUpgradeMaxErr ||
                  updatedCurrentStatemapping?.showUpgradeNoCCErr ||
                  updatedCurrentStatemapping?.showUpgradePMSErr)) ||
              showSettlementCurrencyErrorModal
                ? true
                : false;
            break;

          case 'C_PRICE':
            hasModalOption = updatedCurrentStatemapping && updatedCurrentStatemapping?.showPricingModal ? true : false;
            break;

          case 'U_PRICE':
            hasModalOption = updatedCurrentStatemapping && updatedCurrentStatemapping?.showUpgradeModal ? true : false;
            break;

          case 'U_MAX':
            hasModalOption =
              updatedCurrentStatemapping && updatedCurrentStatemapping?.showUpgradeSuccessMAXInfo ? true : false;
            break;

          case 'U_NA':
            hasModalOption =
              updatedCurrentStatemapping && updatedCurrentStatemapping?.showUpgradeSuccessNAInfo ? true : false;
            break;
        }
        setCurrentStateHasModalOption(hasModalOption);
      }
    };

    const { dataLists } = pageProps;
    const MARSHA: string = marsha?.toUpperCase() ?? '';
    const zList = (dataLists?.zParamList?.toUpperCase() ?? '').split(',');
    const mvList = (dataLists?.mVParamList?.toUpperCase() ?? '').split(',');
    // const msList = (dataLists?.mSParamList ?? '').split(', ');
    const zoneMapping: Record<string, any> = {
      ...dataLists?.zoneValues,
    };

    // reading usage value from data attr from AEM to send correct USAGE in LSP payload for Tier1&2.
    // Also mapping correct SP value in all scenarios payload
    let usageAttr = '';

    if (experienceSegment?.toLowerCase().includes('basic')) {
      usageAttr = 'MEMBER';
    } else if (experienceSegment?.toLowerCase().includes('elite')) {
      usageAttr = 'ELITE';
    }

    // reading valid Z values from data attr to match and assign Z value, in case of invalid or null "GUEST" is default value
    let Z = '';
    if (urlQueryParams?.['Z'] !== null && zList?.includes((urlQueryParams?.['Z'] ?? '')?.toUpperCase())) {
      Z = (urlQueryParams?.['Z'] ?? 'GUEST')?.toUpperCase();
    } else {
      Z = 'GUEST';
    }

    const lrList =
      Z === 'GUEST'
        ? (dataLists?.lrParamList ?? '')
            ?.toUpperCase()
            .split(',')
            ?.filter(lr => lr !== 'AF_AO')
        : (dataLists?.lrParamList ?? '')?.toUpperCase().split(',');

    // reading valid LR values from data attr to match and assign LR value
    let LR = '';
    if (urlQueryParams?.['LR'] && lrList?.includes((urlQueryParams?.['LR'] ?? '')?.toUpperCase())) {
      LR = (urlQueryParams['LR'] ?? '').toUpperCase();
    }

    // reading valid MV values from data attr to match and assign MV value
    let MV = '';
    if (urlQueryParams?.['MV'] !== null && mvList?.includes?.((urlQueryParams?.['MV'] ?? '')?.toUpperCase())) {
      MV = (urlQueryParams['MV'] ?? '').toUpperCase();
    }

    const updatedState = state && state === 'preconnect' ? 'CONNECT' : state;
    const ACTION = (urlQueryParams?.['AT'] as string)?.toUpperCase() ?? updatedState?.toUpperCase() ?? '';
    // reading valid USAGE from URL. Required for payload in nologin/freeconnection&access LSP payload
    const USAGE = (urlQueryParams?.['U'] as string)?.toUpperCase() ?? '';
    // reading duration value from url, default value is 7
    const DURATION = urlQueryParams?.['DURATION'] ?? '7';
    // reading duration initial value from url, default is 1
    const DD = (urlQueryParams?.['DD'] as string)?.toUpperCase() ?? '1';
    const ERR = (urlQueryParams?.['ERR'] as string)?.toUpperCase() ?? '';
    const DOMAIN = (window?.location?.href ?? '')?.split('einterface')?.[0];
    const MS: string | null = (urlQueryParams?.['MS'] as string)?.toUpperCase() ?? null;
    const postEndpoint = urlQueryParams?.['L_URL'] ?? '';
    const U_URLEndpoint = urlQueryParams?.['U_URL'] ?? '';
    const SP = (urlQueryParams?.['SP'] as string)?.toUpperCase() ?? '';
    const ppv5Enrollment = urlQueryParams?.['ppv5Enrollment'] ?? '';

    let APS: string | undefined = undefined;

    //reading valid MS values from data attr to match with URL value and send to LSP Payload
    if ((ACTION === 'PRECONNECT' || ACTION === 'CONNECT' || ACTION === 'CONNECTED') && MS === null) {
      // calculate apsValue
      let apsValue: string | undefined = undefined;
      if (experienceSegment?.toLowerCase()?.includes('member-')) {
        apsValue = 'N';
      } else if (experienceSegment?.toLowerCase()?.includes('basic-')) {
        apsValue = 'N';
      } else if (experienceSegment?.toLowerCase()?.includes('elite-')) {
        apsValue = 'E';
      } else {
        apsValue = '';
      }
      APS = apsValue;
    }

    if (MV || ERR) {
      setShowInternetBarModal(true);
      if (MV) {
        setModalType(MV);
        urlAsPerMapping(MV, ERR);
      }
    } else {
      if (updatedCurrentStatemapping && updatedCurrentStatemapping?.showLoginModal) {
        setCurrentStateHasModalOption(true);
      }
    }

    const loyaltyEnrollmentSourceCode =
      (updatedEnrollmentSourceCode ? updatedEnrollmentSourceCode : dataLists?.loyaltyEnrollmentSourceCode) ?? '';

    return {
      ACTION,
      APS,
      DD,
      DOMAIN,
      DURATION,
      ERR,
      LR,
      MS,
      MV,
      ppv5Enrollment: ppv5Enrollment === 'true' || false,
      SP,
      USAGE,
      Z,
      MARSHA,
      postEndpoint,
      U_URLEndpoint,
      zoneMapping,
      usageAttr,
      loyaltyEnrollmentSourceCode,
    };
  };

  const handleInternetPricingData = (internetPricingData: Record<string, any> | null) => {
    const defaultInternetBarDataValues = initializeInternetBarData();
    let updatedInternetBarDataValues = {
      ...defaultInternetBarDataValues,
    };
    const dataLists = pageProps?.dataLists;
    const mvList = (dataLists?.mVParamList?.toUpperCase() ?? '').split(',');
    let MV = '';
    if (urlQueryParams?.['MV'] !== null && mvList.includes((urlQueryParams?.['MV'] ?? '').toUpperCase())) {
      MV = (urlQueryParams?.['MV'] ?? '').toUpperCase();
    }

    const { data, error } = internetPricingData ?? {};
    setInternetPricingData({ ...(internetPricingData as InternetPricingDataProps) });
    if (!pageProps?.isAuthorMode && data) {
      const {
        enrollmentSourceCode,
        settlementCurrencyData: settlementData,
        uxlGlobalDivision,
        weChatEnrollmentSourceCode,
      } = data;
      const showWeChatModalPopUp = isWeChatEnabled(
        preProcessorResponse?.state,
        uxlGlobalDivision,
        preProcessorResponse?.userState
      );
      setWeChatEnabled(pageProps?.enableCNWeChat && showWeChatModalPopUp);
      setWeChatEnrollmentCode(weChatEnrollmentSourceCode);
      let updatedEnrollmentCode = enrollmentSourceCode;
      if (enrollmentSourceCode) {
        updatedEnrollmentCode = enrollmentSourceCode;
      }

      if (enableLoyaltyConference && !isLoyaltyEnabledForConference) {
        updatedEnrollmentCode = dataLists?.enrollmentSourceCode;
        setUpdatedEnrollmentSourceCode(dataLists?.enrollmentSourceCode);
      } else {
        setUpdatedEnrollmentSourceCode(updatedEnrollmentCode);
      }
      updatedInternetBarDataValues = {
        ...updatedInternetBarDataValues,
        zoneMapping: internetPricingData?.['zoneMapping'],
        loyaltyEnrollmentSourceCode: updatedEnrollmentCode,
      };

      const { pricingInfo, hasError = false } = settlementData;
      setHasError(hasError);
      if (!pricingInfo && hasError && MV && (MV === 'C_PRICE' || MV === 'U_PRICE')) {
        setModalType('INT_ERR');
        setShowSettlementCurrencyErrorModal(true);
        updatedInternetBarDataValues = {
          ...updatedInternetBarDataValues,
          ERR: 'INVALID_PRICING',
        };
      }

      if (pricingInfo) {
        setSettlementCurrencyData({
          ...(pricingInfo as PricingInfoProps),
        });
      }
    } else if (error && MV && (MV === 'C_PRICE' || MV === 'U_PRICE')) {
      setModalType('INT_ERR');
      setShowSettlementCurrencyErrorModal(true);
      let updatedEnrollmentCode = defaultInternetBarDataValues?.loyaltyEnrollmentSourceCode;
      if (data && data?.enrollmentSourceCode) {
        updatedEnrollmentCode = data?.enrollmentSourceCode;
      }
      updatedInternetBarDataValues = {
        ...updatedInternetBarDataValues,
        loyaltyEnrollmentSourceCode: updatedEnrollmentCode,
        ERR: 'UXL',
      };
    }
    setInternetBarDataValues({
      ...updatedInternetBarDataValues,
    });
  };

  const getPricingInfo = useCallback(() => {
    const { isAuthorMode, dataLists } = pageProps;
    let { data, error = null } = propertyInfo;

    if (isAuthorMode) {
      data = { ...settlementCurrenyDetailsMock };
      error = undefined;
    }

    if (!isAuthorMode) {
      let internetPricingData = {};
      const {
        enableLoyalty = false as boolean,
        enableLoyaltyPostConnect = false as boolean,
        enableLoyaltyConference = false as boolean,
        enableLoyaltyConferencePostConnect = false as boolean,
        enrollmentSourceCodeMapping = {} as EnrollmentSourceCodeMapping,
        weChatEnrollmentSourceCodes = [] as Array<EnrollmentSourceCodeDivisionMapping>,
        state = '' as string,
        userSegment = '' as string,
      } = preProcessorResponse || {};

      const zoneMappingDataList: Record<string, any> = {
        MEETING: dataLists?.zoneValues['CONFERENCE'],
        LOBBY: dataLists?.zoneValues['PUBLIC'],
        ...dataLists?.zoneValues,
      };

      if (data && !error) {
        let Z = '';
        const zList = (dataLists?.zParamList?.toUpperCase() ?? '').split(',');

        if (urlQueryParams['Z'] !== null && zList.includes((urlQueryParams['Z'] ?? '').toUpperCase())) {
          Z = (urlQueryParams['Z'] ?? 'GUEST').toUpperCase();
        } else {
          Z = 'GUEST';
        }

        const { enrollmentSourceCode, loyaltyEnrollmentSourceCode } = dataLists;

        const settlementCurrencyPublicZone = settlementCurrencyHandler(
          { ...data },
          'PUBLIC',
          pageProps?.internetPricingModel?.currency
        ).pricingInfo;

        const settlementCurrencyConferenceZone = settlementCurrencyHandler(
          { ...data },
          'CONFERENCE',
          pageProps?.internetPricingModel?.currency
        ).pricingInfo;

        const zoneMappingPricingInfo = {
          PUBLIC: settlementCurrencyPublicZone,
          LOBBY: settlementCurrencyPublicZone,
          CONFERENCE: settlementCurrencyConferenceZone,
          MEETING: settlementCurrencyConferenceZone,
          GUEST: settlementCurrencyHandler({ ...data }, 'GUEST', pageProps?.internetPricingModel?.currency).pricingInfo,
        };

        const publicZonePropertyTier = getZonePropertyTier(zoneMappingPricingInfo['PUBLIC']) ?? 'SF';
        const conferenceZonePropertyTier = getZonePropertyTier(zoneMappingPricingInfo['CONFERENCE']) ?? 'SF';

        const updatedZoneMapping = {
          PUBLIC: publicZonePropertyTier,
          LOBBY: publicZonePropertyTier,
          CONFERENCE: conferenceZonePropertyTier,
          MEETING: conferenceZonePropertyTier,
          GUEST: getZonePropertyTier(zoneMappingPricingInfo['GUEST']) ?? 'SF',
        };

        setComputedZoneMapping({ ...updatedZoneMapping });

        setServiceData({ zoneMapping: updatedZoneMapping, zoneZ: Z }, false, false, {});

        const propertyTier = updatedZoneMapping?.['GUEST'];

        const value: EnrollmentSourceCodeMapping = enrollmentSourceCodeMapping ?? {};

        let finalEnrollmentSourceCode = '';
        let weChatEnrollmentSourceCode: string | undefined = '';
        let loyaltyAgeExperience: string | undefined = '';
        let sessionStorageBrandTier = '';

        if (typeof window !== 'undefined' && isACDLenabled) {
          sessionStorageBrandTier = JSON.parse(
            window.sessionStorage.getItem('oldDataLayer') as string
          )?.prop_brand_tier;
        }

        const brandTier = datalayer?.['prop_brand_tier'] ?? sessionStorageBrandTier;
        if (
          preProcessorResponse?.ageExperienceConfig &&
          preProcessorResponse?.ageExperienceConfig?.userExperience &&
          preProcessorResponse?.ageExperienceConfig?.enrollmentcode
        ) {
          loyaltyAgeExperience = preProcessorResponse?.ageExperienceConfig?.userExperience;
          finalEnrollmentSourceCode = preProcessorResponse?.ageExperienceConfig?.enrollmentcode;
        } else {
          ({ finalEnrollmentSourceCode, weChatEnrollmentSourceCode, loyaltyAgeExperience } =
            getRegionEnrollmentSourceCode(
              value,
              data,
              enableLoyalty ||
                enableLoyaltyPostConnect ||
                enableLoyaltyConference ||
                enableLoyaltyConferencePostConnect ||
                isLoyaltyEnabledForConference
                ? enableLoyalty ||
                    enableLoyaltyPostConnect ||
                    enableLoyaltyConference ||
                    enableLoyaltyConferencePostConnect ||
                    isLoyaltyEnabledForConference
                : urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true',
              propertyTier,
              loyaltyEnrollmentSourceCode ?? '',
              enrollmentSourceCode ?? '',
              state,
              userSegment,
              weChatEnrollmentSourceCodes ?? [],
              (brandTier as string) ?? ''
            ));
        }

        handleLinks(finalEnrollmentSourceCode, updatedZoneMapping, Z);

        const settlementData = settlementCurrencyHandler({ ...data }, Z, pageProps?.internetPricingModel?.currency);
        internetPricingData = {
          data: {
            weChatEnrollmentSourceCode: weChatEnrollmentSourceCode,
            enrollmentSourceCode: finalEnrollmentSourceCode,
            userExperience: loyaltyAgeExperience,
            settlementCurrencyData: settlementData,
            uxlGlobalDivision: data?.basicInformation?.globalDivision,
            uxlCountryCode: data?.contactInformation?.address?.country?.code,
          },
          zoneMapping: updatedZoneMapping ? updatedZoneMapping : zoneMappingDataList,
          error: null,
        };

        if (!(preProcessorResponse && (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference)))) {
          setRenderInternetBarComponent(renderPreAgeExp);
        } else {
          setRenderInternetBarComponent(renderAgeExperience(loyaltyAgeExperience));
        }
      } else if (error) {
        internetPricingData = {
          data: null,
          zoneMapping: zoneMappingDataList,
          error: error,
        };
      }
      handleInternetPricingData(internetPricingData);
    }
  }, [preProcessorResponse, datalayer]);

  useEffect(() => {
    const lrParam = urlQueryParams?.['LR'];
    const mvParam = urlQueryParams?.['MV'];
    if (currentStateMapping && lrParam === 'STRICT_AO') {
      setUpdatedCurrentStateMapping({
        ...currentStateMapping,
        noLoginInterfaceModal: false,
        showLoginModal: false,
        showAccessModal: true,
        accessModal: {
          default: true,
          acErrorMessage: mvParam && mvParam === 'ACCESSERR' ? true : false,
        },
        loginModal: {
          connectNowLabel: false,
          default: false,
          separatorLabel: false,
        },
      });
      setIsNoLoginInterfaceModal(false);
    }
  }, [currentStateMapping, urlQueryParams?.['LR'], urlQueryParams?.['MV']]);

  useEffect(() => {
    const renderWelcomeModalPopup = () => {
      const { MV, ppv5Enrollment, Z, LR, loyaltyEnrollmentSourceCode } = internetBarDataValues;
      const isSFRoomLastNameValidationFlag = getSFRoomLastNameValidationFlag(
        enrollmentSourceCodeMapping?.loyalty,
        propertyInfo?.data?.basicInformation?.globalDivision,
        propertyInfo?.data?.contactInformation?.address?.country?.code
      );
      if (
        ppv5Enrollment?.toString()?.toLowerCase() === 'true' &&
        typeof window !== 'undefined' &&
        getEnrolledUserFlag(loyaltyEnrollmentSourceCode)
      ) {
        setCurrentStateHasModalOption(true);
        setIsNoLoginInterfaceModal(false);
        if (LR && LR?.toString()?.toUpperCase() === 'STRICT_AO') {
          setShowInternetBarModal(true);
          setModalType(MV ? MV : 'ACCESS');
        } else if (Z?.toUpperCase() === 'GUEST') {
          setShowInternetBarModal(true);
          if (computedZoneMapping?.[Z ?? ''] === 'SF') {
            if (!(LR?.toString()?.toUpperCase() === 'AF_AC' || LR?.toString()?.toUpperCase() === 'AF_MO')) {
              if (!isSFRoomLastNameValidationFlag && !LR) {
                setModalType('LOYALTY_WELCOME');
              } else {
                setModalType(MV ? MV : 'LOGIN');
                let showSeparator = true;
                if (LR) {
                  showSeparator =
                    updatedCurrentStatemapping.loginModal.userAccessCodeLabel ||
                    updatedCurrentStatemapping.loginModal.requestAccessCodeLabel;
                }
                setLoyaltyCurrentStateMapping({
                  ...updatedCurrentStatemapping,
                  showLoginModal: MV ? updatedCurrentStatemapping?.showLoginModal : true,
                  loginModal: {
                    ...updatedCurrentStatemapping.loginModal,
                    connectNowLabel: true,
                    default: true,
                    separatorLabel: showSeparator,
                  },
                });
              }
            } else {
              setModalType(MV ? MV : 'LOYALTY_WELCOME');
            }
          } else if (computedZoneMapping?.[Z ?? ''] === 'SP') {
            setShowInternetBarModal(true);
            if (updatedCurrentStatemapping?.noLoginInterfaceModal) {
              if (LR) {
                setModalType(MV ? MV : 'LOGIN');
                let showSeparator = true;
                if (LR?.toString()?.toUpperCase() === 'AF_MO') {
                  showSeparator = false;
                }
                setLoyaltyCurrentStateMapping({
                  ...updatedCurrentStatemapping,
                  showLoginModal: MV ? updatedCurrentStatemapping?.showLoginModal : true,
                  loginModal: {
                    ...updatedCurrentStatemapping.loginModal,
                    connectNowLabel: true,
                    default: true,
                    separatorLabel: showSeparator,
                    userAccessCodeLabel: LR?.toString()?.toUpperCase() === 'AF_AC',
                  },
                });
              } else {
                setModalType('LOYALTY_WELCOME');
              }
            } else {
              setModalType(MV ? MV : 'LOGIN');
            }
          } else if (computedZoneMapping?.[Z ?? ''] === 'FP' || computedZoneMapping?.[Z ?? ''] === 'PP') {
            setModalType(MV ? MV : 'LOGIN');
            setLoyaltyCurrentStateMapping({
              ...updatedCurrentStatemapping,
              showLoginModal: MV ? updatedCurrentStatemapping?.showLoginModal : true,
              loginModal: {
                ...updatedCurrentStatemapping.loginModal,
                default: true,
              },
            });
          }
        } else {
          if (LR && LR?.toUpperCase() !== 'TCTC' && !updatedCurrentStatemapping?.noLoginInterfaceModal) {
            setShowInternetBarModal(true);
            if (LR?.toUpperCase() === 'AF_AO' || LR?.toUpperCase() === 'STRICT_AO') {
              setModalType('ACCESS');
            } else {
              setModalType(MV ? MV : 'LOGIN');
            }
          } else {
            setShowInternetBarModal(true);
            if (updatedCurrentStatemapping?.noLoginInterfaceModal) {
              setModalType('LOYALTY_WELCOME');
            } else {
              setModalType(MV ? MV : 'LOGIN');
            }
          }
        }
      }
    };

    if (internetBarDataValues && computedZoneMapping) {
      renderWelcomeModalPopup();
    }
  }, [currentStateMapping, internetBarDataValues, pageProps, computedZoneMapping, updatedCurrentStatemapping]);

  useEffect(() => {
    const checkPropertyLinks = (item: string): boolean => {
      return (
        item.length > 1 &&
        !item.includes('einterface/v5') &&
        !item.includes('tel:') &&
        // eslint-disable-next-line no-script-url
        !item.includes('javascript:void(0)')
      );
    };

    const anchorList = (document.getElementsByTagName('a') ?? []) as unknown as HTMLAnchorElement[];
    if (openPpv5LinkInSameTab(sameTab, cna)) {
      for (const element of anchorList) {
        if (element?.hasAttribute('target')) {
          element?.removeAttribute('target');
        }
      }
    } else {
      const bottomFoldElements: NodeListOf<HTMLElement> = document.querySelectorAll(
        '.amenities-list, .key-amenities, .nearby-things-to-do'
      );
      bottomFoldElements?.forEach((ele: HTMLElement) => {
        const elements = (ele.getElementsByTagName('a') ?? []) as unknown as HTMLAnchorElement[];
        for (const element of elements) {
          const item = element.getAttribute('href') ?? '';
          const role = element.getAttribute('role') ?? '';
          // check if link is not ppv5 or telephone
          if (checkPropertyLinks(item) && role?.toLowerCase() !== 'tab') {
            element.setAttribute('target', '_blank');
          }
        }
      });
    }
  }, [cna, sameTab, state]);

  useEffect(() => {
    document.querySelector('.m-global-nav-top-center')?.querySelector('a')?.querySelector('.lazy-image')?.remove();
    const globalNav = document.querySelector('.m-global-nav-top-center')?.querySelector('a');
    globalNav?.setAttribute('aria-label', 'Marriott Bonvoy');
    const globalNavLogo = document.createElement('DIV');
    document.querySelector('.m-global-nav-main-menu')?.querySelector('a')?.querySelector('.lazy-image')?.remove();
    const globalNavMenu = document.querySelector('.m-global-nav-main-menu')?.querySelector('a');
    globalNavMenu?.setAttribute('aria-label', 'Marriott Bonvoy');
    const globalNavMenuLogo = document.createElement('DIV');
    if (document.documentElement.lang.toUpperCase().indexOf('CN') > -1) {
      globalNavLogo.setAttribute('class', 't-program-logo-cn-s');
      globalNavMenuLogo.setAttribute('class', 't-program-logo-cn-s');
    } else {
      globalNavLogo.setAttribute('class', 't-program-logo-xs');
      globalNavMenuLogo.setAttribute('class', 't-program-logo-xs');
    }
    globalNav?.appendChild(globalNavLogo);
    globalNavMenu?.appendChild(globalNavMenuLogo);
  }, []);

  useEffect(() => {
    // @TODO - remove the globalNavLanguageClosebtn logic once it is fixed from AEM
    const globalNavLanguageClosebtn = document
      .querySelector('.m-global-nav-language-container .m-global-nav-language-content')
      ?.querySelector('.m-global-nav-language-close .icon-clear.icon-inverse + .sr-only');
    if (globalNavLanguageClosebtn) {
      (globalNavLanguageClosebtn as HTMLElement).textContent =
        pageProps?.requestAccessCodeModel?.racClosingLabel ?? 'Close';
    }
  }, [pageProps?.requestAccessCodeModel?.racClosingLabel]);

  useEffect(() => {
    if (weChatEnabled) {
      if (navigator && navigator?.language?.toLowerCase()?.includes('zh')) {
        setIsCaptiveCNPortal(true);
      } else {
        setShowInternetBarModal(true);
        setModalType('WE_CHAT');
      }
    }
  }, [weChatEnabled, weChatEnrollmentCode]);

  useEffect(() => {
    if (preProcessorResponse && preProcessorResponse?.propertyInfo) {
      getPricingInfo();
    }
  }, [preProcessorResponse, getPricingInfo]);

  useEffect(() => {
    setLoyaltyForConferenceZone(isLoyaltyEnabledForConference);
  }, [isLoyaltyEnabledForConference]);

  const renderInternetBarModalComponent = () => {
    return (
      showInternetBarModal && (
        <InternetBarModal
          enrollmentSourceCode={updatedEnrollmentSourceCode}
          weChatEnrollmentSourceCode={weChatEnrollmentCode}
          dataLists={pageProps?.dataLists}
          showInternetBarModal={showInternetBarModal}
          internetBarDataValues={{ ...internetBarDataValues }}
          modalType={modalType}
          accessModal={pageProps?.accessCodeModel}
          login={pageProps?.loginModel}
          currentStateMapping={
            urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true' &&
            typeof window !== 'undefined' &&
            getEnrolledUserFlag(internetBarDataValues?.['loyaltyEnrollmentSourceCode'])
              ? { ...loyaltyCurrentStateMapping }
              : { ...updatedCurrentStatemapping }
          }
          handleCloseBtnClick={handleCloseBtnClick}
          wifiUpgradeGENError={pageProps?.wifiUpgradeErrorFieldsBean}
          wifiUpgradeLR={pageProps?.wifiUpgradeLrFieldsBean}
          wifiUpgradeMAX={pageProps?.wifiUpgradeMaxFieldsBean}
          wifiUpgradeNoCC={pageProps?.wifiUpgradeCreditFieldsBean}
          wifiUpgradePMS={pageProps?.wifiUpgradePmsFieldsBean}
          wifiUpgradeSuccessMAX={pageProps?.wifiUpgradeSuccessMaxFieldsBean}
          wifiUpgradeSuccessNA={pageProps?.wifiUpgradeSuccessNaFieldsBean}
          weChatFieldsBean={pageProps?.weChatFieldsBean}
          requestAccessCodeModal={pageProps?.requestAccessCodeModel}
          showSettlementCurrencyErrorModal={showSettlementCurrencyErrorModal}
          internetPricingModel={{
            ...pageProps?.internetPricingModel,
            internetPricingInfo:
              modalType === 'C_PRICE' || modalType === 'U_PRICE'
                ? { ...(settlementCurrencyData as PricingInfoProps) }
                : {
                    tierOneFee: '0',
                    tierTwoFee: '0',
                  },
          }}
          currencyUxlFailureErrorFieldsBean={pageProps?.currencyUxlFailureErrorFieldsBean}
          invalidCurrencyErrorFieldsBean={pageProps?.invalidCurrencyErrorFieldsBean}
          handleLoginBonvoyLookupService={handleLoginBonvoyLookupService}
        />
      )
    );
  };

  return (
    <StyledInternetBar data-testid="internet-box" data-component-name="o-ppv5-internet-box" className="relative">
      {currentStateHasModalOption &&
      !isNoLoginInterfaceModal &&
      modalType &&
      (modalType === 'C_PRICE' || modalType === 'U_PRICE') ? (
        (settlementCurrencyData || (!settlementCurrencyData && hasError)) && (
          <div data-testid={`internet-bar-modal-${state}`}>{renderInternetBarModalComponent()}</div>
        )
      ) : (
        <div data-testid={`internet-bar-modal-${state}`}>{renderInternetBarModalComponent()}</div>
      )}

      <div className="internet-bar-container color-scheme1 mx-md-auto">{renderInternetBarComponent}</div>
      {isCaptiveCNPortal && <InternetBarWeChatPseudoLink weChatEnrollmentSourceCode={weChatEnrollmentCode} />}
    </StyledInternetBar>
  );
};

export const InternetBarConfig = {
  emptyLabel: 'InternetBarComponent',
  isEmpty: (_props: MappedComponentProperties) => false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/hero-banner`,
} as Config<MappedComponentProperties>;

export const InternetBarEditable = (props: InternetBarProps) => (
  <EditableComponent config={InternetBarConfig} {...props}>
    <InternetBar {...props} />
  </EditableComponent>
);
